import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";
import { Link } from "react-router-dom";

import { cn } from "@/utils/cn";

import Icon from "../icon/icon";

const buttonVariants = cva(
  "inline-flex appearance-none place-content-center items-center space-x-2 rounded-full border font-medium whitespace-nowrap transition-all focus:ring-2 focus:outline-hidden disabled:pointer-events-none disabled:cursor-default disabled:opacity-75 disabled:select-none rtl:space-x-reverse",
  {
    variants: {
      variant: {
        default:
          "bg-primary-500 hover:bg-primary-400 disabled:hover:bg-primary-500 dark:disabled:hover:bg-primary-500 dark:hover:bg-primary-600 border-transparent focus:bg-primary-500 text-gray-100 focus:ring-primary-300",
        secondary:
          "border-transparent bg-primary-200 dark:bg-primary-900 hover:bg-primary-100 dark:hover:bg-primary-800 focus:bg-primary-200 dark:focus:bg-primary-900 text-primary-500 dark:text-primary-400 dark:focus:ring-primary-900",
        outline:
          "bg-transparent border-gray-300 dark:border-gray-800 hover:border-gray-500 dark:hover:border-gray-700 focus:border-gray-300 dark:focus:border-gray-800 text-gray-900 dark:text-gray-100 focus:ring-gray-500 dark:focus:ring-gray-700",
        destructive:
          "border-transparent bg-danger-100 dark:bg-danger-900 text-danger-600 dark:text-danger-200 hover:bg-danger-600 hover:text-gray-100 dark:hover:text-gray-100 dark:hover:bg-danger-500 focus:ring-danger-500",
        ghost:
          "border-transparent bg-transparent text-primary-600 dark:text-primary-400 dark:bg-transparent hover:bg-gray-200 dark:hover:bg-gray-800/50",
        accent:
          "border-transparent bg-secondary-500 hover:bg-secondary-400 focus:bg-secondary-500 text-gray-100 focus:ring-secondary-400",
        link: "bg-transparent border-transparent text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50!",
      },
      size: {
        sm: "px-3 py-1.5 text-xs leading-4",
        default: "px-4 py-2 text-sm",
        lg: "px-6 py-3 text-base",
      },
      block: {
        true: "flex w-full justify-center",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonVariants = VariantProps<typeof buttonVariants>;
type ButtonVariant = NonNullable<ButtonVariants["variant"]>;
type ButtonSizes = NonNullable<ButtonVariants["size"]>;

interface ButtonProps extends Omit<ButtonPrimitive.Props, "size">, ButtonVariants {
  /** Makes the button into an anchor tag, if provided. */
  href?: string;
  /** URL to an SVG icon to render inside the button. */
  icon?: string;
  iconClassName?: string;
  /** Action when the button is clicked. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  target?: React.HTMLAttributeAnchorTarget | undefined;
  /** Text inside the button. Takes precedence over `children`. */
  text?: React.ReactNode;
  /** Makes the button into a navlink, if provided. */
  to?: string;
  /** Whether this button should submit a form by default. */
  type?: "button" | "submit";
  /** Custom test ID for testing. */
  testId?: string;
  ref?: React.Ref<HTMLButtonElement>;
}

/** Customizable button element with various themes. */
const Button = ({
  block,
  className,
  href,
  icon,
  iconClassName = "h-4 w-4",
  size,
  target,
  text,
  to,
  type = "button",
  variant,
  testId = "button",
  children,
  ...props
}: ButtonProps) => {
  const body = text || children;

  const renderButton = () => (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, block }), className)}
      type={type}
      data-testid={testId}
      {...props}
    >
      {icon && <Icon src={icon} className={iconClassName} />}

      {body && <span>{body}</span>}
    </ButtonPrimitive>
  );

  if (to) {
    return (
      <Link to={to} tabIndex={-1} className="inline-flex">
        {renderButton()}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} target={target} tabIndex={-1} className="inline-flex">
        {renderButton()}
      </a>
    );
  }

  return renderButton();
};

export {
  Button as default,
  Button,
  buttonVariants,
  type ButtonProps,
  type ButtonSizes,
  type ButtonVariant,
};
